import {
    useEffect, useState, useRef, useCallback,
    useMemo,
} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
    DataGrid,
    GridToolbarContainer, // keep these for now. Switching to a custom toolbar is more involved.
    useGridApiContext,
    useGridSelector,
    gridPageCountSelector,
    gridPaginationModelSelector,
    GridToolbarQuickFilter,
    GridViewStreamIcon,
    GridTableRowsIcon,
    GridViewHeadlineIcon,
    gridDensitySelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';

import { applyWordsAsCommands, getAliasForCompound } from 'BMapsSrc/cmds/cmds_helpers';
import { IndigoSvg } from 'BMapsSrc/IndigoSvg';
import useEventBrokerSubscription from './common/helpers/useEventBrokerSubscription';
import DialogContentSection from './common/DialogContentSection';
import TooltipInfo from './common/TooltipInfo';
import { DialogCancelButton, DialogSubmitButton } from './common/DialogActionButtons';
import { setSkipGreeting, isSkippingGreeting } from '../util/speech_utils';
import BMapsTooltip from './BMapsTooltip';
import { withTooltip } from './common/helpers';

function AliasToolbar(props) {
    return (
        <GridToolbarContainer {...props}>
            <div style={{
                display: 'flex', justifyContent: 'space-between', width: '100%', overflow: 'hidden',
            }}
            >
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <GridToolbarQuickFilter style={{ width: '100%' }} />
                </div>
                <DensityIconButton />

            </div>
        </GridToolbarContainer>
    );
}

function DensityIconButton() {
    const apiRef = useGridApiContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const density = useGridSelector(apiRef, gridDensitySelector);

    const openMenu = (event) => setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    const setDensity = (value) => {
        apiRef.current.setDensity(value);
        closeMenu();
    };

    const icon = useMemo(() => {
        switch (density) {
            case 'compact':
                return <GridViewHeadlineIcon />;
            case 'comfortable':
                return <GridViewStreamIcon />;
            case 'standard':
            default:
                return <GridTableRowsIcon />;
        }
    }, [density]);

    return (
        <>
            <BMapsTooltip title="Table Density">
                <IconButton aria-label="Table Density" onClick={openMenu}>
                    {icon}
                </IconButton>
            </BMapsTooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={() => setDensity('compact')}>Compact</MenuItem>
                <MenuItem onClick={() => setDensity('standard')}>Standard</MenuItem>
                <MenuItem onClick={() => setDensity('comfortable')}>Comfortable</MenuItem>
            </Menu>
        </>
    );
}

function AliasFooter() {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const { page } = useGridSelector(apiRef, gridPaginationModelSelector);

    if (pageCount <= 1) return null;

    return (
        <MuiPagination
            count={pageCount}
            page={page + 1}
            onChange={(_, value) => apiRef.current.setPage(value - 1)}
            size="small"
            sx={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}
        />
    );
}

export function CommandConsole({ open }) {
    const [messages, setMessages] = useState([]);
    const [heard, setHeard] = useState('');
    const [notice, setNotice] = useState('');
    const [typedCommand, setTypedCommand] = useState('');
    const messagesEndRef = useRef(null);
    const [shorten, setShorten] = useState(isSkippingGreeting);

    const updateCookie = (evt) => {
        const shortened = evt.target.checked;
        setShorten(shortened);
        setSkipGreeting(shortened);
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [messages]);

    const updateCommandText = useCallback((evtName, args) => {
        if (args.inputWords) {
            setHeard(args.inputWords);
        }

        if (args.interpretedCmds) {
            const { results, error } = args.interpretedCmds;
            if (results.length === 0) {
                if (error) console.log(`Failed to interpret command: ${error}`);
                setNotice('Command not recognized');
            } else {
                setNotice('');
                setMessages((prev) => [
                    ...prev,
                    {
                        content: <CmdMessageContent results={results} />,
                        role: 'assistant',
                        id: prev.length,
                    },
                ]);
            }
        }
    }, []);

    useEventBrokerSubscription('commandInput', updateCommandText);

    // typed input
    const submitTypedCommand = useCallback(async () => {
        applyWordsAsCommands(typedCommand.trim());
        setTypedCommand('');
    }, [typedCommand]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <div style={{ overflowY: 'auto', flex: '0 1 auto', minHeight: 0 }}>
                <DialogContentSection
                    titleVariant="subtitle2"
                    collapsible
                    defaultExpand
                    disablePaddingTop
                    title="Voice Control"
                    TitleProps={{ style: { padding: '0 .5rem' } }}
                >
                    <>
                        <Typography style={{ fontSize: 'smaller', margin: '.5rem 0' }}>
                            Voice control is available by clicking the mic button in
                            the top right corner.
                            While the mic is active, BMaps will listen to and interpret
                            everything it hears.
                            Voice commands are best understood if you start with &quot;Gibbs,&quot;
                            followed by a command,
                            {' eg, '}
                            <span style={{ fontStyle: 'italic' }}>Gibbs, pin Compound B.</span>
                            You can also use the textbox at the bottom of the command panel
                            to manually type in commands.
                        </Typography>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={shorten}
                                    onChange={updateCookie}
                                />
                            )}
                            label="Skip voice control greeting"
                        />
                    </>

                </DialogContentSection>

                {/* CompoundAliasTable includes a collapsible DialogContentSection and preamble */}
                <CompoundAliasTable open={open} />
            </div>

            <div style={{
                padding: '1rem',
                paddingTop: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 0 max(25vh, 300px)',
                borderTop: '2px solid lightgray',
            }}
            >
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.5rem',
                        border: '1px solid lightgray',
                        borderRadius: '4px 4px 0 0',
                        background: 'whitesmoke',
                    }}
                >
                    {messages.length === 0 ? (
                        <Typography style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                            No commands have been performed.
                        </Typography>
                    ) : (
                        messages.map((message) => (
                            <Box
                                key={message.id}
                                ref={
                                message.id === messages.length - 1
                                    ? messagesEndRef
                                    : null
                            }
                                style={{
                                    marginBottom: '4px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems:
                                    message.role === 'user'
                                        ? 'flex-end'
                                        : 'flex-start',
                                }}
                            >
                                <strong style={{ fontStyle: 'italic' }}>
                                    {message.role === 'user' ? 'You:' : 'Command:'}
                                </strong>
                                <Box
                                    sx={{
                                        border: '1px solid lightgray',
                                        borderRadius: '.3rem',
                                        maxWidth: '90%',
                                        wordWrap: 'break-word',
                                        background:
                                        message.role === 'user'
                                            ? 'white'
                                            : 'mintcream',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '0.3rem',
                                        marginTop: '2px',
                                        '& li': { marginLeft: '1rem' },
                                        '& pre': { whiteSpace: 'pre-wrap' },
                                    }}
                                >
                                    {message.content}
                                </Box>
                            </Box>
                        ))
                    )}
                </div>
                <div
                    style={{
                        padding: '0.5rem',
                        border: '1px solid lightgray',
                        borderRadius: '0 0 4px 4px',
                        background: 'white',
                    }}
                >
                    {heard && (
                    <Typography
                        style={{ fontStyle: 'italic', marginBottom: '0.25rem' }}
                    >
                        <strong>User: </strong>
                        {heard}
                    </Typography>
                    )}
                    {!!notice && (
                    <Typography color="error">
                        {notice}
                    </Typography>
                    )}
                    <CommandTextFieldWithTooltip
                        tooltip="To view available commands, type /help or ask 'What commands are available?'"
                        command={typedCommand}
                        setCommand={setTypedCommand}
                        submitCommand={submitTypedCommand}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                        <DialogSubmitButton
                            onClick={() => submitTypedCommand()}
                        >
                            Submit
                        </DialogSubmitButton>
                        <DialogCancelButton
                            onClick={() => {
                                setTypedCommand('');
                                setMessages([]);
                                setHeard('');
                            }}
                        >
                            Clear
                        </DialogCancelButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

const CommandTextFieldWithTooltip = withTooltip(CommandTextField);

function CommandTextField({ setCommand, submitCommand, command }) {
    return (
        <TextField
            fullWidth
            size="small"
            placeholder="Type a command..."
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitCommand();
                }
            }}
            style={{ margin: '0.5rem 0' }}
        />
    );
}

/**
 * Component to display the results of a single user command message,
 * which could include multiple interpreted commands with associated errors/warnings.
 */
function CmdMessageContent({ results }) {
    return (
        <Typography component="div" style={{ whiteSpace: 'pre-wrap' }}>
            {results.map(({ displayText, errors }, i) => (
                <div
                    key={`cmd-message-content-${i.toString()}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.3em' }}
                >
                    <span>{displayText}</span>
                    {!!errors?.length && (
                        <TooltipInfo title={errors.join('\n')} warning color="error" />
                    )}
                </div>
            ))}
        </Typography>
    );
}

function CompoundAliasTable({ open }) {
    const [aliasRows, setAliasRows] = useState([]);
    const [gridKey, setGridKey] = useState(0);

    useEffect(() => {
        // Updating the key each time the table is opened forces the DataGrid to remount,
        // ensuring column header widths are recalculated.
        if (open) setGridKey((k) => k + 1);
    }, [open]);

    const multiProtein = App.Workspace.getLoadedProteins().length > 1;

    const aliasTooltip = (row) => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4em',
            background: 'white',
            color: 'black',
            padding: '0.6em',
            borderRadius: '4px',
        }}
        >
            <div style={{ fontWeight: 'bold', fontSize: '0.95em' }}>{row.compound}</div>
            <div style={{ fontSize: '0.85em', color: '#555' }}>
                <span style={{ fontWeight: 600 }}>Alias: </span>
                {row.alias}
            </div>
            {multiProtein && (
                <div style={{ fontSize: '0.85em', color: '#555' }}>
                    <span style={{ fontWeight: 600 }}>Target: </span>
                    {row.target}
                </div>
            )}
            <div style={{ borderTop: '1px solid #ddd', paddingTop: '0.4em' }}>
                <IndigoSvg
                    smiles={row.smiles}
                    autoUpdate
                    style={{
                        height: '8rem',
                        width: '12rem',
                        padding: 0,
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                    }}
                    alt={`2D structure of ${row.compound}`}
                />
            </div>
        </div>
    );

    const aliasTooltipProps = {
        noMaxWidth: true,
        slotProps: {
            tooltip: { sx: { padding: '0.3em', maxWidth: '50vw' } },
        },
    };

    const aliasColumns = [
        ...(multiProtein ? [{
            field: 'target',
            headerName: 'Target',
            sortable: false,
            filterable: true,
            resizable: false,
            renderCell: ({ value, row }) => (
                <BMapsTooltip {...aliasTooltipProps} title={aliasTooltip(row)}>
                    <span>{value}</span>
                </BMapsTooltip>
            ),
            renderHeader: () => (
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    Target
                </Typography>
            ),
        }] : []),
        {
            field: 'compound',
            headerName: 'Compound',
            sortable: false,
            filterable: true,
            flex: 1,
            resizable: false,
            renderCell: ({ value, row }) => (
                <div style={{ width: '100%', paddingRight: '1em', overflow: 'hidden' }}>
                    <BMapsTooltip {...aliasTooltipProps} title={aliasTooltip(row)}>
                        <span
                            style={{
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                            }}
                        >
                            {value}
                        </span>
                    </BMapsTooltip>
                </div>
            ),
            renderHeader: () => (
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    Compound
                </Typography>
            ),
        },
        {
            field: 'alias',
            headerName: 'Alias',
            sortable: false,
            filterable: true,
            resizable: false,
            renderCell: ({ value, row }) => (
                <BMapsTooltip {...aliasTooltipProps} title={aliasTooltip(row)}>
                    <span>{value}</span>
                </BMapsTooltip>
            ),
            renderHeader: () => (
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    Alias
                </Typography>
            ),
        },
    ];

    const buildAliasRows = useCallback(() => {
        const loadedCompounds = App.Workspace.getLoadedCompounds();
        const rows = loadedCompounds.map((compound) => {
            const label = getAliasForCompound(compound);
            const { mapCase } = App.getDataParents(compound);
            return {
                id: label,
                compound: compound.displayName,
                alias: label,
                smiles: compound.getUnnamedSmiles(),
                target: mapCase?.getShortName() ?? '',
            };
        });
        setAliasRows(rows);
    }, []);

    useEffect(() => {
        buildAliasRows();
        EventBroker.subscribe('compoundChanged', buildAliasRows);
        return () => {
            EventBroker.unsubscribe('compoundChanged', buildAliasRows);
        };
    }, [buildAliasRows]);

    if (aliasRows.length === 0) return null;

    return (
        <DialogContentSection
            titleVariant="subtitle2"
            collapsible
            defaultExpand
            disablePaddingTop
            title="Compound Aliases"
            TitleProps={{ style: { padding: '0 .5rem' } }}
            titleIcon={(
                <TooltipInfo title={(
                    <>
                        BMaps uses ChatGPT to interpret human language as application commands.
                        For privacy, your workspace compound names will not be sent to ChatGPT.
                        Instead, use these aliases to refer to compounds in your commands.
                        <br />
                        <br />
                        <span style={{ fontStyle: 'italic' }}>
                            Example: Switch to compound J and show it in spheres
                        </span>
                    </>
                )}
                />
            )}
        >
            <DataGrid
                key={gridKey}
                rows={aliasRows}
                columns={aliasColumns}
                disableColumnMenu={false}
                disableRowSelectionOnClick
                pageSizeOptions={[5, 10, 15]}
                showToolbar
                initialState={{
                    density: 'compact',
                    pagination: {
                        paginationModel: { pageSize: 5, page: 0 },
                    },
                }}
                slots={{
                    toolbar: AliasToolbar,
                    footer: AliasFooter,
                }}
                slotProps={{
                    panel: { placement: 'bottom-end' },
                }}
            />
        </DialogContentSection>
    );
}
